import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select, {type SelectChangeEvent} from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {useTheme} from "@mui/material/styles";
import * as React from "react";
import {useTranslation} from "react-i18next";
import {chartPalette} from "../../components/chartColors";
import {InsightsLegend, LegendEntry} from "../../kit/InsightsLegend";
import {InsightsColumn, InsightsTable} from "../../kit/InsightsTable";
import {SeriesCard} from "../../kit/SeriesCard";
import {CHART_HEIGHT} from "../../kit/insightsChart";
import type {InsightsLoading} from "../../kit/insightsLoading";
import type {CrmChannelsTimeSeriesV4} from "../../models/crm-v4";
import CrmSeriesLinePlot, {type SeriesPlotRow} from "./CrmSeriesLinePlot";
import {useBucketLabels} from "./crmTrendBuckets";

interface ChannelsTrendCardProps {
    data?: CrmChannelsTimeSeriesV4;
    loading?: InsightsLoading;
}

/**
 * The PMS ships raw channel codes because the property defines them, so the readable names live here. An unknown
 * code falls back to the code itself rather than being hidden — a channel nobody has named is still a channel.
 */
const CHANNEL_LABELS: Record<string, string> = {
    DIR: "Walk-in",
    EMA: "E-mail",
    GUEST_APP: "Guest app",
    MYF: "Website",
    RG: "Request",
    STAFF: "Staff",
    TEL: "Phone",
};

export function ChannelsTrendCard({data, loading}: ChannelsTrendCardProps) {
    const {t, i18n} = useTranslation();
    const theme = useTheme();
    const [excludedChannelIds, setExcludedChannelIds] = React.useState<string[]>([]);

    const locale = i18n.resolvedLanguage || i18n.language;
    const channelLabel = (id: string, name: string) => {
        const known = CHANNEL_LABELS[id];
        if (id === "GUEST_APP" || id === "STAFF") return t(known);
        return known ? `${t(known)} (${id})` : name;
    };

    const availableSeries = data?.series ?? [];
    const availableIds = new Set(availableSeries.map(series => series.id));
    const selectedSeries = availableSeries.filter(series => !excludedChannelIds.includes(series.id));
    const selectedIds = selectedSeries.map(series => series.id);
    const hasData = availableSeries.some(series => series.values.some(value => value != null && value > 0));

    const points = data?.points ?? [];
    const buckets = useBucketLabels(points, data?.granularity);
    const valueFormatter = new Intl.NumberFormat(locale, {maximumFractionDigits: 0});
    const axisFormatter = new Intl.NumberFormat(locale, {notation: "compact", maximumFractionDigits: 1});
    const formatBookings = (value: number) => `${valueFormatter.format(value)} ${t("bookings")}`;

    const palette = chartPalette(theme);
    const colorByChannelId = new Map(availableSeries.map((series, index) => [
        series.id,
        palette[index % palette.length],
    ]));

    const rows: SeriesPlotRow[] = buckets.map((bucket, index) => {
        const row: SeriesPlotRow = {key: bucket.key, label: bucket.label, full: bucket.full};

        selectedSeries.forEach(series => {
            row[series.id] = series.values[index] ?? null;
        });

        return row;
    });

    const plotted = selectedSeries.map(series => ({
        id: series.id,
        label: channelLabel(series.id, series.name),
        color: colorByChannelId.get(series.id) ?? theme.palette.text.secondary,
    }));

    const columns: InsightsColumn<SeriesPlotRow>[] = [
        {key: "bucket", label: t("Time range"), kind: "label", cell: row => row.full},
        ...plotted.map(series => ({
            key: series.id,
            label: series.label,
            kind: "value" as const,
            cell: (row: SeriesPlotRow) => {
                const value = row[series.id];

                return value == null ? "—" : formatBookings(Number(value));
            },
        })),
    ];

    const handleSelectionChange = (event: SelectChangeEvent<string[]>) => {
        const nextSelected = typeof event.target.value === "string"
            ? event.target.value.split(",")
            : event.target.value;

        setExcludedChannelIds(previous => [
            ...previous.filter(id => !availableIds.has(id)),
            ...availableSeries.filter(series => !nextSelected.includes(series.id)).map(series => series.id),
        ]);
    };

    const renderSelection = (ids: string[]) => {
        if (ids.length === availableSeries.length) return t("All channels");
        if (ids.length === 0) return t("No channels selected");
        if (ids.length === 1) {
            const series = availableSeries.find(item => item.id === ids[0]);
            return series ? channelLabel(series.id, series.name) : ids[0];
        }
        return t("{{count}} channels", {count: ids.length});
    };

    const emptyMessage = availableSeries.length === 0 || !hasData
        ? t("No data in the selected period")
        : selectedSeries.length === 0
            ? t("Select at least one channel")
            : undefined;

    return (
        <SeriesCard
            title={t("Channels over time")}
            hint={t("Bookings that arrived through each channel, by arrival date")}
            loading={loading}
            actions={hasData ? (
                <FormControl size={"small"} sx={{minWidth: 220}}>
                    <InputLabel id={"insights-channel-series-select-label"}>{t("Channels")}</InputLabel>
                    <Select<string[]>
                        multiple
                        labelId={"insights-channel-series-select-label"}
                        label={t("Channels")}
                        value={selectedIds}
                        onChange={handleSelectionChange}
                        renderValue={renderSelection}
                        disabled={loading != null}
                        sx={{height: 36, "& .MuiSelect-select": {py: 0.75}}}
                        MenuProps={{PaperProps: {sx: {maxHeight: 360}}}}
                    >
                        {availableSeries.map(series => (
                            <MenuItem key={series.id} value={series.id}>
                                <Checkbox checked={selectedIds.includes(series.id)}/>
                                <ListItemText primary={channelLabel(series.id, series.name)}/>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            ) : undefined}
            empty={emptyMessage == null ? undefined : (
                <Stack alignItems={"center"} justifyContent={"center"} sx={{height: CHART_HEIGHT, px: 3}}>
                    <Typography variant={"body2"} color={"text.secondary"} textAlign={"center"}>
                        {emptyMessage}
                    </Typography>
                </Stack>
            )}
            legend={(
                <InsightsLegend>
                    {plotted.map(series => (
                        <LegendEntry key={series.id} color={series.color} label={series.label}/>
                    ))}
                </InsightsLegend>
            )}
            table={<InsightsTable label={t("Channels over time")} columns={columns} rows={rows} rowKey={row => row.key}/>}
            chart={(
                <CrmSeriesLinePlot
                    rows={rows}
                    series={plotted}
                    formatValue={formatBookings}
                    formatTick={value => axisFormatter.format(value)}
                />
            )}
        />
    );
}

export default ChannelsTrendCard;
